import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import type {JOB_CUSTOM_FIELD} from 'src/types/crm';

import {useDispatch, useSelector} from 'react-redux';

import * as reduxApi from 'src/utils/redux-api-v2';

import {formatMomentDateWithFormat} from 'src/utils/date-time';

import {
  CollapsibleCard,
  CollapsibleCardContent,
  CollapsibleCardAction,
} from '@spaced-out/ui-design-system/lib/components/CollapsibleCard';
import {
  BodyMedium,
  SubTitleMedium,
  SubTitleSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Shimmer} from '@spaced-out/ui-design-system/lib/components/Shimmer';
import {Chip} from '@spaced-out/ui-design-system/lib/components/Chip';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';

import {Banner} from '@spaced-out/ui-design-system/lib/components/Banner';
import ViewCustomFields from 'src/components/crm/lib/custom-fields/view-custom-fields';
import {CRM_PERMISSIONS} from 'src/utils/crm-new/common';
import CustomFormPanel from 'src/components/crm/applicants/body/tabComponents/forms/custom-form-panel';
import {selectHasCrmPermission, selectUserInfo} from 'src/selectors/crm';

import css from './internal-response-form.css';
import {
  toastApi,
  TOAST_SEMANTIC,
  ToastTitle,
  Toast,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {Link} from '@spaced-out/ui-design-system/lib/components/Link';


const InternalResponseForm = ({
  formInterview,
  applicationId,
  isPublicLink,
  candidateName = '',
}: {
  formInterview: {
    stepId: number;
    status: string;
    candidateId: number;
    jobId: number;
    jobInternalForm: {
      validationStatus: string;
      validatorId: number;
      validationComment: string;
      updatedOn: string;
      id: number;
      customFields: {
        id: number;
        fieldType: string;
        questionTag: string;
        value: string;
        placeholderFormat?: string;
        forPdf: boolean;
        options: string[];
        customFieldId: number;
        users: {
          fullName: string;
          id: number;
        }[];
      }[];
      customForms: {
        sections: Array<{
          name: string;
          fields: Array<JOB_CUSTOM_FIELD>;
        }>;
      }[];
    };
  };
  applicationId: number;
  isPublicLink?: boolean;
  candidateName?: string;
}) => {
  const {t, language} = useLabelTranslation();
  const userInfo = useSelector(selectUserInfo);
  const editCandidateForm = selectHasCrmPermission(
    userInfo,
    CRM_PERMISSIONS.EDIT_CANDIDATE_FORM,
  );
  const dispatch = useDispatch();
  const [isCardOpen, setIsCardOpen] = React.useState(true);
  const [loadingStep, setLoadingStep] = React.useState(false);
  const {jobInternalForm, stepId, status} = formInterview;
  const {validationStatus, validatorId, validationComment, id} =
    jobInternalForm;
  const [stepName, setStepName] = React.useState<any>(null);
  const [isCustomFormPanelOpen, setIsCustomFormPanelOpen] =
    React.useState(false);

  const renderAnswer = (field: JOB_CUSTOM_FIELD) => {
    if (!jobInternalForm.customFields) {
      return <BodyMedium>{`'${t('NA', 'N/A')}'`}</BodyMedium>;
    }
    const customField = jobInternalForm.customFields.find(
      (customField) => customField.id === field.customFieldId,
    );
    if (!customField) {
      return <BodyMedium>{`'${t('NA', 'N/A')}'`}</BodyMedium>;
    }

    return <ViewCustomFields field={customField} />;
  };

  React.useEffect(() => {
    const fetchStep = async () => {
      try {
        setLoadingStep(true);
        const stepData = await dispatch(
          reduxApi.get(
            `api/job-steps/${stepId}`,
            {},
            {
              apiPath: '/trmapi/',
            },
          ),
        );
        setStepName(stepData.name);
      } catch (e: any) {
        throw e;
      } finally {
        setLoadingStep(false);
      }
    };

    if (stepId) {
      fetchStep();
    }
  }, [stepId]);

  const approvalStatusString = `${t(
    'Submitted_by',
    'Submitted by',
  )} ${candidateName} ${t('on', 'on')} ${formatMomentDateWithFormat(
    jobInternalForm.updatedOn ?? '',
    'd MMMM, YYYY',
    language,
  )}`;

  const handleDownloadForm = async () => {
    const {candidateId, jobId, stepId} = formInterview;
    const headers = new Headers();
    headers.append('Content-Type', 'application/json;charset=UTF-8');
    headers.append('Accept', 'application/json, text/plain, */*');

    toastApi.show(
      <Toast semantic={TOAST_SEMANTIC.information}>
        <ToastTitle>{`${t('Downloading', 'Downloading')}...`}</ToastTitle>
      </Toast>,
      {
        autoClose: true,
      },
    );

    try {
      const response = await fetch(
        '/trmapi/api/candidate/form/download-as-zip',
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            candidateIds: [candidateId],
            jobId,
            bulkActionFilters: {
              jobId,
              stepId,
            },
          }),
        },
      );

      const disposition = response.headers.get('content-disposition');
      let filename = t('Candidate_Formzip', 'Candidate_Form.zip');

      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename="?(.*?)"?$/);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = filename;

      document.body?.appendChild(a);
      a.click();
      document.body?.removeChild(a);
      URL.revokeObjectURL(url);
      toastApi.show(
        <Toast semantic={TOAST_SEMANTIC.success}>
          <ToastTitle>
            {`${t(
              'Downloaded_Successfully',
              'Downloaded Successfully',
            )}: ${filename}`}
          </ToastTitle>
        </Toast>,
        {
          autoClose: true,
        },
      );
    } catch (error: any) {
      toastApi.show(
        <Toast semantic={TOAST_SEMANTIC.danger}>
          <ToastTitle>
            {t('Error_downloading_zip_file', 'Error downloading zip file')}
          </ToastTitle>
        </Toast>,
        {
          autoClose: true,
        },
      );
      console.error('Error downloading zip file', error);
    }
  };
  return (
    <>
      <CollapsibleCard
        classNames={{
          wrapper: '',
        }}
        isOpen={isCardOpen}
        onChange={() => setIsCardOpen(!isCardOpen)}
        title={
          loadingStep ? (
            <Shimmer height="20px" width="200px" />
          ) : (
            <div className={css.candidateResponseFormTitleContainer}>
              <div className={css.internalResponseFormTitle}>
                <SubTitleSmall>{stepName}</SubTitleSmall>
                <Chip semantic="information">
                  {t('Internal_Form', 'Internal Form')}
                </Chip>
              </div>
              <Link
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleDownloadForm();
                }}
                underline={false}
              >
                {t('Download_Form', 'Download Form')}
              </Link>
            </div>
          )
        }
      >
        <CollapsibleCardContent className={css.cardContentWithBanner}>
          {validationStatus === 'APPROVED' ? (
            <Banner semantic="success">
              {t('Approved_by', 'Approved by')}
            </Banner>
          ) : validationStatus === 'DECLINED' ? (
            <Banner semantic="danger">{t('Declined', 'Declined')}</Banner>
          ) : (
            <Banner semantic="information">{approvalStatusString}</Banner>
          )}

          <div className={css.internalResponseCardContent}>
            {jobInternalForm.customForms &&
              jobInternalForm.customForms[0].sections &&
              jobInternalForm.customForms[0].sections.map((section) => {
                return (
                  <div className={css.formDisplaySectionContainer}>
                    <SubTitleMedium>{section.name}</SubTitleMedium>
                    <div className={css.lineBreak} />

                    {section.fields
                      .filter(
                        (field) => field.show !== 'OFF' && field.customField,
                      )
                      .map((field) => (
                        <div className={css.customField}>
                          <SubTitleSmall>{field.questionTag}</SubTitleSmall>
                          {renderAnswer(field)}
                        </div>
                      ))}
                  </div>
                );
              })}
          </div>
        </CollapsibleCardContent>
        <CollapsibleCardAction>
          <div className={css.cardActionContainer}>
            {editCandidateForm && (
              <Button
                type="tertiary"
                size="small"
                iconLeftName="pencil"
                onClick={() => setIsCustomFormPanelOpen(true)}
              >
                {t('Edit', 'Edit')}
              </Button>
            )}
          </div>
        </CollapsibleCardAction>
      </CollapsibleCard>
      {isCustomFormPanelOpen ? (
        <CustomFormPanel
          isCandidate={true}
          // @ts-expect-error - TS2741 - Property 'interviewCandidateFormField' is missing in type '{ stepId: number; status: string; jobInternalForm: { validationStatus: string; validatorId: number; validationComment: string; updatedOn: string; id: number; customFields: { id: number; fieldType: string; ... 6 more ...; users: { ...; }[]; }[]; customForms: { ...; }[]; }; }' but required in type 'FormInterview'.
          formInterview={formInterview}
          isOpen={isCustomFormPanelOpen}
          onClose={() => {
            setIsCustomFormPanelOpen(false);
          }}
          formType="INTERNAL_FORM"
        />
      ) : null}
    </>
  );
};

export default InternalResponseForm;
